import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AuthorizedPage from "../../component/AuthorizedPage";
import { Role } from "../../domain/Role";
import AssistantApi, { ChatMessage, AssistantFile } from "../../api/AssistantApi";
import "./AssistantChat.scss";
import maskot from "./maskot.png";

const STORAGE_KEY = "ozisik-assistant-history";

// Kullaniciya AI jargonu yok: model isimleri "Hizli/Dengeli/En Akilli"
const MODEL_OPTIONS = [
  { value: "hizli", label: "⚡ Hızlı" },
  { value: "dengeli", label: "⚖️ Dengeli" },
  { value: "akilli", label: "🧠 En Akıllı" },
];

// Tool teknik adi -> kullaniciya gosterilecek Turkce etiket (BE'deki AssistantTool.name() ile birebir)
const TOOL_LABELS: Record<string, string> = {
  depolanabilir_urun_ara: "Depolanabilir ürünlerde arandı",
  satilabilir_urun_ara: "Satılabilir ürünlerde arandı",
  stok_hareket_gecmisi: "Stok hareket geçmişi getirildi",
  senkronizasyon_durumu: "Senkronizasyon durumu okundu",
  sevkiyatlari_listele: "Sevkiyatlar listelendi",
  araclari_listele: "Araçlar listelendi",
  soforleri_listele: "Şoförler listelendi",
  satis_irsaliyeleri_listele: "Satış irsaliyeleri listelendi",
  depolari_listele: "Depolar listelendi",
};

const SAMPLE_QUESTIONS = [
  "TOST peynirlerinin stok durumunu tablo halinde raporla",
  "AHİR PEYNİR TOST ürününün son 7 günlük stok hareket raporunu çıkar",
  "Teslim edilen sevkiyatları araç, şoför ve tonaj bilgisiyle özetle",
  "Yolda olan sevkiyatlar hangileri, hangi araçlarla?",
  "Satış irsaliyelerini müşteri bazında özetle",
];

interface Msg extends ChatMessage {
  toolsUsed?: string[];
  files?: AssistantFile[];
}

interface State {
  messages: Msg[];
  input: string;
  model: string;
  loading: boolean;
}

class AssistantChat extends React.Component<any, State> {
  scrollRef = React.createRef<HTMLDivElement>();

  constructor(props: any) {
    super(props);
    let saved: Msg[] = [];
    try {
      saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch (e) {
      saved = [];
    }
    this.state = { messages: saved, input: "", model: "dengeli", loading: false };
  }

  componentDidMount() {
    // Açılışta geçmiş varsa en alta (son konuşma görünsün). İçerik (markdown/görsel)
    // yerleşsin diye küçük gecikme.
    setTimeout(() => {
      const box = this.scrollRef.current;
      if (box) box.scrollTop = box.scrollHeight;
    }, 80);
  }

  // Sadece mesaj eklenince/yükleme değişince kaydır (tuş vuruşunda DEĞİL) ve
  // yalnızca sohbet kutusunu kaydır — sayfayı asla. Yeni cevap gelince cevabın
  // başına hizala ki kullanıcı baştan okusun.
  componentDidUpdate(_prevProps: any, prevState: State) {
    const grew = this.state.messages.length !== prevState.messages.length;
    const loadingChanged = this.state.loading !== prevState.loading;
    if (!grew && !loadingChanged) return;

    const box = this.scrollRef.current;
    if (!box) return;

    const last = this.state.messages[this.state.messages.length - 1];
    if (grew && last && last.role === "assistant") {
      const rows = box.querySelectorAll(".assistant-row");
      const el = rows[rows.length - 1] as HTMLElement | undefined;
      if (el) {
        box.scrollTop += el.getBoundingClientRect().top - box.getBoundingClientRect().top - 12;
        return;
      }
    }
    // kullanıcı mesajı veya "düşünüyor": en alta (mesaj + animasyon görünsün)
    box.scrollTop = box.scrollHeight;
  }

  persist(messages: Msg[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }

  send = async (text?: string) => {
    const message = (text ?? this.state.input).trim();
    if (!message || this.state.loading) return;

    const history: ChatMessage[] = this.state.messages.map((m) => ({ role: m.role, content: m.content }));
    const withUser: Msg[] = [...this.state.messages, { role: "user", content: message }];
    this.setState({ messages: withUser, input: "", loading: true });
    this.persist(withUser);

    const res = await AssistantApi.chat(message, history, this.state.model);

    let assistant: Msg;
    if (res.success && res.data) {
      assistant = { role: "assistant", content: res.data.reply || "(boş cevap)", toolsUsed: res.data.toolsUsed || [], files: res.data.files || [] };
    } else {
      assistant = { role: "assistant", content: "⚠️ Bir hata oluştu: " + (res.error?.message || "asistan yanıt veremedi.") };
    }
    const finalMsgs = [...withUser, assistant];
    this.setState({ messages: finalMsgs, loading: false });
    this.persist(finalMsgs);
  };

  openFile = async (f: AssistantFile) => {
    try {
      await AssistantApi.openFile(f.id);
    } catch (e) {
      alert("Dosya açılamadı. Rapor süresi dolmuş olabilir (30 dk), lütfen tekrar oluşturun.");
    }
  };

  downloadFile = async (e: React.MouseEvent, f: AssistantFile) => {
    e.stopPropagation();
    try {
      await AssistantApi.downloadFile(f.id, f.name);
    } catch (err) {
      // Dosya suresi dolmus (30 dk) olabilir; kullaniciyi bilgilendir.
      alert("Dosya indirilemedi. Rapor süresi dolmuş olabilir, lütfen tekrar oluşturun.");
    }
  };

  clearChat = () => {
    this.setState({ messages: [] });
    localStorage.removeItem(STORAGE_KEY);
  };

  onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  };

  render() {
    const { messages, input, model, loading } = this.state;
    return (
      <AuthorizedPage role={Role.SUPER_ADMIN}>
        <div className="assistant-wrap">
          <div className="assistant-header">
            <div className="assistant-id">
              <div className="assistant-avatar"><img src={maskot} alt="Damla" /></div>
              <div>
                <div className="assistant-name">
                  Damla <span className="assistant-badge">YENİ</span>
                </div>
                <div className="assistant-sub">Özışık'ın yapay zeka ajanı</div>
              </div>
            </div>
            {messages.length > 0 && (
              <button className="assistant-clear" onClick={this.clearChat}>Sohbeti temizle</button>
            )}
          </div>

          <div className="assistant-scroll" ref={this.scrollRef}>
            {messages.length === 0 ? (
              <div className="assistant-empty">
                <div className="assistant-empty-icon"><img src={maskot} alt="Damla" /></div>
                <h5 className="assistant-empty-title">Merhaba, ben Damla 👋</h5>
                <p className="assistant-empty-text">Stok, sevkiyat, ürün… ne merak ediyorsanız sorun, raporlayayım.</p>
                <div className="assistant-samples">
                  {SAMPLE_QUESTIONS.map((q) => (
                    <button key={q} className="assistant-sample" onClick={() => this.send(q)}>{q}</button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`assistant-row ${m.role}`}>
                  {m.role === "assistant" && <div className="assistant-avatar sm"><img src={maskot} alt="" /></div>}
                  <div className={`assistant-bubble ${m.role}`}>
                    {m.role === "assistant" ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    ) : (
                      m.content
                    )}
                    {m.toolsUsed && m.toolsUsed.length > 0 && (
                      <div className="assistant-tools">
                        {Array.from(new Set(m.toolsUsed)).map((t) => (
                          <span key={t} className="assistant-tool-chip">{TOOL_LABELS[t] || t}</span>
                        ))}
                      </div>
                    )}
                    {m.files && m.files.length > 0 && (
                      <div className="assistant-files">
                        {m.files.map((f) => (
                          <div
                            key={f.id}
                            className="assistant-file-chip"
                            onClick={() => this.openFile(f)}
                            title="Önizlemek için tıkla"
                          >
                            <span className="assistant-file-ico">📄</span>
                            <span className="assistant-file-name">{f.name}</span>
                            <span className="assistant-file-hint">önizle</span>
                            <button
                              className="assistant-file-dl"
                              onClick={(e) => this.downloadFile(e, f)}
                              title="İndir"
                            >
                              İndir
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}

            {loading && (
              <div className="assistant-row assistant">
                <div className="assistant-avatar sm"><img src={maskot} alt="" /></div>
                <div className="assistant-bubble assistant assistant-thinking">
                  <span className="assistant-milk" aria-hidden="true">
                    <span className="milk-drop"></span>
                    <span className="milk-pool"></span>
                  </span>
                  <span className="assistant-thinking-text">düşünüyor…</span>
                </div>
              </div>
            )}
          </div>

          <div className="assistant-composer-wrap">
            <div className="assistant-composer">
              <textarea
                className="assistant-textarea"
                placeholder="Bir şey sorun…"
                value={input}
                onChange={(e) => this.setState({ input: e.target.value })}
                onKeyDown={this.onKeyDown}
                rows={1}
              />
              <div className="assistant-composer-bar">
                <select
                  className="assistant-model"
                  value={model}
                  onChange={(e) => this.setState({ model: e.target.value })}
                >
                  {MODEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <button
                  className="assistant-send"
                  onClick={() => this.send()}
                  disabled={loading || !input.trim()}
                  aria-label="Gönder"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a.993.993 0 00-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.61c0 .71.73 1.2 1.39.91z" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="assistant-hint">Enter gönderir · Shift+Enter yeni satır · asistan yalnızca okuma yapabilir</div>
          </div>
        </div>
      </AuthorizedPage>
    );
  }
}

export default AssistantChat;
